import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Gem,
  Search,
  Loader2,
  AlertCircle,
  Package
} from "lucide-react";

const AdminCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" or "edit"
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    visible: true
  });
  
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return null;
    }
    return {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      } else {
        setError("Failed to fetch categories");
      }
    } catch (err) {
      setError("Error loading categories: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories`, {
        method: "POST",
        headers,
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess("Category created successfully!");
        setShowModal(false);
        resetForm();
        fetchCategories();
      } else {
        const data = await response.json();
        setError(data.detail || "Failed to create category");
      }
    } catch (err) {
      setError("Error creating category: " + err.message);
    }
  };

  const handleUpdateCategory = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories/${editingCategory._id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess("Category updated successfully!");
        setShowModal(false);
        resetForm();
        fetchCategories();
      } else {
        const data = await response.json();
        setError(data.detail || "Failed to update category");
      }
    } catch (err) {
      setError("Error updating category: " + err.message);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories/${category._id}`, {
        method: "DELETE",
        headers
      });

      if (response.ok) {
        setSuccess("Category deleted successfully!");
        fetchCategories();
      } else {
        const data = await response.json();
        setError(data.detail || "Failed to delete category");
      }
    } catch (err) {
      setError("Error deleting category: " + err.message);
    }
  };

  const openCreateModal = () => {
    setModalMode("create");
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setModalMode("edit");
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      visible: category.visible !== false  // Default to true if undefined
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      visible: true
    });
    setEditingCategory(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (modalMode === "create") {
      handleCreateCategory();
    } else {
      handleUpdateCategory();
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Clear alerts after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Filter categories based on search
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="outline" 
              onClick={() => navigate("/admin/dashboard")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>
          
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Category Management
              </h1>
              <p className="text-gray-600">
                Manage product categories for your online store
              </p>
            </div>
            
            <Button onClick={openCreateModal} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Category
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {success && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <AlertCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        )}

        {/* Search Bar */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Categories List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gem className="w-5 h-5" />
              Categories ({filteredCategories.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-600">Loading categories...</span>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="text-center py-20">
                <Gem className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {categories.length === 0 ? "No categories found" : "No matching categories"}
                </h3>
                <p className="text-gray-600 mb-4">
                  {categories.length === 0 
                    ? "Create your first category to organize your products." 
                    : "Try adjusting your search terms."}
                </p>
                {categories.length === 0 && (
                  <Button onClick={openCreateModal}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCategories.map((category) => {
                  // Handle visible field - default to true if undefined
                  const isVisible = category.visible !== false;
                  
                  return (
                  <Card key={category._id} className={`border-2 ${!isVisible ? 'border-gray-300 bg-gray-50' : 'border-gray-200'}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Gem className="w-5 h-5 text-purple-600" />
                          <h3 className="font-semibold text-gray-900">{category.name}</h3>
                        </div>
                        <div className="flex items-center gap-2">
                          {isVisible ? (
                            <Eye className="w-4 h-4 text-green-600" title="Visible" />
                          ) : (
                            <EyeOff className="w-4 h-4 text-gray-400" title="Hidden" />
                          )}
                          <Badge variant={isVisible ? "default" : "secondary"}>
                            {isVisible ? "Visible" : "Hidden"}
                          </Badge>
                        </div>
                      </div>
                      
                      {category.description && (
                        <p className="text-sm text-gray-600 mb-4">{category.description}</p>
                      )}
                      
                      <div className="flex items-center gap-2 mb-4">
                        <Package className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {category.product_count || 0} products
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(category)}
                          className="flex-1"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteCategory(category)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={category.product_count > 0}
                          title={category.product_count > 0 ? "Cannot delete category with products" : "Delete category"}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gem className="w-5 h-5" />
                  {modalMode === "create" ? "Add New Category" : "Edit Category"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Category Name *
                    </label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter category name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional category description"
                      rows={3}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="visible"
                      checked={formData.visible}
                      onChange={(e) => setFormData({ ...formData, visible: e.target.checked })}
                      className="rounded"
                    />
                    <label htmlFor="visible" className="text-sm font-medium text-gray-700">
                      Visible in store
                    </label>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowModal(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1">
                      {modalMode === "create" ? "Create" : "Update"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminCategoriesPage;